<template>
    <DsModal
        data-qa="sequence-modal"
        class="sequence-modal"
        :class="{ 'is-sidebar-open': isSidebarOpen, 'is-email-data-open': showReportingView && hasEmailCell, 'hidden': !!showConfigModal }"
        show-close
        size="fluid"
        is-open
        :title="$t('funnel.sidebar.then.header.label')"
        :sub-title="currentSequenceCell.value.name"
        @close="onCloseSequence"
    >
        <template #header>
            <section class="vue-right-controls">
                <HeaderSequenceActionsMenu
                    v-if="!showReportingView"
                    :sequence-is-ready="currentSequenceCell.value.ready === '1'"
                    @toggle-sequence-ready="onToggleSequenceReady"
                    @copy-from-campaign="onCopyFromCampaign"
                    @sequence-rename="onSequenceRename"
                />
                <DsFilledButton class="done-button" data-qa="done-button" @click="onDoneSequence">
                    {{ $t('global.done') }}
                </DsFilledButton>
            </section>
        </template>
        <div class="sequence-modal-content">
            <KonvaCampaignStage
                ref="sequenceStage"
                is-sequence
                :debug-mode="debugMode"
                :selected-sequence="currentSequenceCell.id"
                :report-data="reportData"
                :display-widgets="false"
                :show-sidebar="isSidebarOpen"
                :show-reporting-view="showReportingView"
                :enabled-cell-types="enabledCellTypes"
                :enabled-integrations="enabledIntegrations"
                :is-inline-add-open="isInlineAddOpen"
                :cells="cells"
                :published-cells="publishedCells"
                :preset="preset"
                :selected-cells="selectedCells"
                :flags-info="flagsInfo"
                :editable-name-data="editableNameData"
                :show-config-modal="showConfigModal"
                :beta-features-enabled="betaFeaturesEnabled"
                :initial-zoom-level="sequenceZoomLevel"
                @on-open-filter-modal="(event) => $emit('on-open-filter-modal', event)"
                @on-update-filters="(event) => $emit('on-update-filters', event)"
                @open-element-list="() => $emit('open-element-list')"
                @open-between-add="(event) => $emit('open-between-add', event)"
                @open-inline-add="(event) => $emit('open-inline-add', event)"
                @close-inline-add="(event) => $emit('close-inline-add', event)"
                @close-sequence="onCloseSequence"
                @click-cell="onClickCell"
                @click-stage="onClickStage"
                @set-selected-cells="(selectedCells) => $emit('set-selected-cells', selectedCells)"
                @copy-cells="copyCells"
                @move-cells="moveCells"
                @rename-cell="renameCell"
                @delete-cells="deleteCells"
                @add-cell="addCell"
                @open-config-modal="openConfigModal"
                @set-sequence-id="setSequenceId"
                @resize="onResize"
                @open-automation-feedback-modal="openFeedbackModal"
                @set-editable-name-data="(editableNameData) => $emit('set-editable-name-data', editableNameData)"
                @update-note="(event) => $emit('update-note', event)"
                @update-note-color="(event) => $emit('update-note-color', event)"
                @update-sidebar-collapsed="(isCollapsed) => $emit('update-sidebar-collapsed', isCollapsed)"
            />

            <KonvaEmailStats
                v-if="areEmailsStatsAvailable && emailStats !== null"
                :report-email-stats="emailStats"
                :preset="preset"
            />
        </div>
    </DsModal>
</template>
<script>
import KonvaCampaignStage from './KonvaCampaignStage.vue';
import HeaderSequenceActionsMenu from '../header/HeaderSequenceActionsMenu.vue';
import {
    PROCESSES,
    PRESET_FILTERS,
} from '../utils/editor.constants';
import KonvaEmailStats from '../reporting/KonvaEmailStats.vue';
import { flowEmailReporting } from './FlowReporting.api';

export default {
    components: {
        KonvaCampaignStage,
        HeaderSequenceActionsMenu,
        KonvaEmailStats,
    },
    props: {
        debugMode: {
            type: Boolean,
            default: false,
        },
        publishedCells: {
            type: Array,
            required: true,
        },
        enabledCellTypes: {
            type: Array,
            required: true,
        },
        enabledIntegrations: {
            type: Array,
            default: () => [],
        },
        flagsInfo: {
            type: Object,
            default: () => ({}),
        },
        funnelInfo: {
            type: Object,
            required: true,
        },
        currentSequenceCell: {
            type: Object,
            required: true,
        },
        isSpaApp: {
            type: Boolean,
            default: false,
        },
        reportData: {
            type: Object,
            default: () => {},
        },
        editableNameData: {
            type: Object,
            default: () => {},
        },
        isSidebarOpen: {
            type: Boolean,
            default: false,
        },
        isInlineAddOpen: {
            type: Boolean,
            default: false,
        },
        showReportingView: {
            type: Boolean,
            default: false,
        },
        cells: {
            type: Array,
            default: () => [],
        },
        selectedCells: {
            type: Array,
            default: () => [],
        },
        preset: {
            type: String,
            default: PRESET_FILTERS.LAST_7_DAYS,
        },
        dateRange: {
            type: Array,
            required: true,
        },
        showConfigModal: {
            type: Boolean,
            default: false,
        },
        betaFeaturesEnabled: {
            type: Boolean,
            required: true,
        },
        sequenceZoomLevel: {
            type: Number,
            required: true,
        },
    },
    emits: [
        'add-cell',
        'click-cell',
        'click-stage',
        'copy-cells',
        'delete-cells',
        'move-cells',
        'on-close-sequence',
        'open-automation-feedback-modal',
        'open-config-modal',
        'open-copy-from-automation',
        'open-edit-sequence-name',
        'on-open-filter-modal',
        'on-update-filters',
        'open-element-list',
        'open-between-add',
        'open-inline-add',
        'close-inline-add',
        'set-selected-cells',
        'set-editable-name-data',
        'update-note',
        'rename-cell',
        'resize',
        'set-sequence-id',
        'toggle-sequence-ready',
        'update-cell-value',
        'update-note-color',
        'update-sidebar-collapsed',
    ],
    data() {
        return {
            emailStats: {
                type: Object,
                default: () => ({}),
            },
        };
    },
    computed: {
        hasEmailCell() {
            return !!this.cells.find((cell) => {
                return (cell.meta.parent === this.currentSequenceCell.meta.id)
                    && [PROCESSES.PROCESS_BARD_EMAIL, PROCESSES.PROCESS_UNLAYER_EMAIL].includes(cell.type);
            });
        },

        areEmailsStatsAvailable() {
            return this.hasEmailCell && !!this.emailStats?.data;
        },
    },
    async mounted() {
        if (this.showReportingView && this.hasEmailCell) {
            await this.loadEmailStats();
        }
    },
    methods: {
        setSequenceId(eventData) {
            this.$emit('set-sequence-id', eventData);
        },

        onClickCell({ cell, event }) {
            this.$emit('click-cell', { cell, event });
        },

        onClickStage({ event }) {
            this.$emit('click-stage', { event });
        },

        onResize(eventData) {
            this.$emit('resize', eventData);
        },

        async loadEmailStats() {
            try {
                const graphXml = this.funnelInfo?.draftXml;
                const { funnelId } = this.funnelInfo;
                const flowId = this.currentSequenceCell.id;
                const customSelected = this.preset === PRESET_FILTERS.CUSTOM;
                const customStartDate = this.dateRange[0].toLocaleDateString('en-us', { year: 'numeric', month: '2-digit', day: '2-digit' }).replaceAll('/', '-');
                const customEndDate = this.dateRange[1].toLocaleDateString('en-us', { year: 'numeric', month: '2-digit', day: '2-digit' }).replaceAll('/', '-');

                const params = {
                    funnelId, flowId, customSelected, customStartDate, customEndDate, graphXml,
                };

                this.emailStats = await flowEmailReporting(params);
            } catch (error) {
                this.emailStats = null;
                // eslint-disable-next-line no-console
                console.error(error);
            }
        },
        copyCells(eventData) {
            this.$emit('copy-cells', eventData);
        },
        moveCells(eventData) {
            this.$emit('move-cells', eventData);
        },
        renameCell({ cell, name, mxGeometry }) {
            this.$emit('rename-cell', { cell, name, mxGeometry });
        },
        deleteCells() {
            this.$emit('delete-cells');
        },
        addCell(eventData) {
            this.$emit('add-cell', eventData);
        },
        openConfigModal(eventData) {
            this.$emit('open-config-modal', eventData);
        },
        openFeedbackModal() {
            this.$emit('open-automation-feedback-modal');
        },
        onCloseSequence() {
            this.$emit('on-close-sequence');
        },

        onDoneSequence() {
            this.$emit('update-cell-value', { cell: this.currentSequenceCell, isReady: true });

            this.onCloseSequence();
        },

        onToggleSequenceReady() {
            this.$emit('toggle-sequence-ready', this.currentSequenceCell);
        },

        onCopyFromCampaign() {
            this.$emit('open-copy-from-automation');
        },

        onSequenceRename() {
            this.$emit('open-edit-sequence-name');
        },

        centerCellIfOutOfView(cell) {
            this.$refs.sequenceStage?.centerCellIfOutOfView?.(cell);
        },
    },
};

</script>
<style scoped lang="scss">
.sequence-modal ::v-deep .modal-body {
    padding: 0;
}

@media (min-width: 950px) {
    .sequence-modal ::v-deep .modal-dialog.fluid {
        max-width: 90%;
        width: var(--modal-width, 90%);
        min-height: auto;
    }
}

.sequence-modal {
    --modal-padding: 0;
}
.sequence-modal:not(.is-sidebar-open).is-email-data-open .sequence-modal-content {
    display: grid;
    grid-template-columns: 1fr 390px;
}
.sequence-modal-content {
    height: calc(100vh - 200px);
}

.campaign-stage {
    height: 100%;
}
.campaign-stage.show-sidebar {
    display: grid;
    grid-template-columns: auto 1fr;
}
.vue-right-controls {
    margin-right: $spacing-125;
    align-items: center;
    display: flex;
    justify-content: flex-end;
    grid-gap: $spacing-100;
}
.sequence-modal ::v-deep .ds-modal-header-title {
    color: $color-ink-900;
    font-family: 'Poppins Bold';
    font-size: $font-size-xl;
    font-weight: 700;
    line-height: $line-height-lg;
}
</style>
